import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';

import { ORBIT_LAYER_IDS } from '@/components/map/utils/mapLayerOrder';
import { readEntityClusterSummary } from '@/components/map/utils/entityClusterProperties';
import type { EntityClusterSummary } from '@/components/map/utils/entityClusterProperties';
import { useClusterCardHover } from '@/hooks/useClusterCardHover';
import type { ClusterCardHoverOptions } from '@/hooks/useClusterCardHover';

export interface PoiClusterInteractionState {
    /**
     * The split-model breakdown read from the cluster's entity accumulators. A cluster
     * without them (a source this build never creates) is ignored rather than shown as
     * an empty card.
     */
    hoveredEntitySummary: EntityClusterSummary | null;
    /** Geographic [lng, lat] of the hovered cluster — the MapLibre `<Popup>` anchor. */
    hoveredClusterLngLat: [number, number] | null;
    onClusterCardEnter: () => void;
    onClusterCardLeave: () => void;
}

const SINGLE_CLIENT_CLUSTER_OPTIONS: ClusterCardHoverOptions = {
    // Both cluster layers show the breakdown card: the network-level clusters (below
    // POI_MIN_ZOOM) and the viewport clusters (POI_MIN_ZOOM..clusterMaxZoom).
    layerIds: [ORBIT_LAYER_IDS.poiClusters, ORBIT_LAYER_IDS.poiNetworkClusters],
    // Only clusters carry a card, and only entity-source clusters carry the family
    // accumulators the card reads; anything else (unreachable with the entity sources) is
    // ignored. Cluster ids are only unique per source, so the key includes the layer id.
    keyFor: (feature) => {
        const clusterId = feature.properties['cluster_id'] as number | undefined;
        if (clusterId == null) return null;
        if (typeof feature.properties['count_pois'] !== 'number') return null;
        return `${feature.layer.id}:${String(clusterId)}`;
    },
    readSummary: readEntityClusterSummary,
    // No click of its own: the cluster click ease-zooms to expand in useEntitiesLayer, which
    // owns the sources. The shared hook still drops the card the moment the click lands.
};

/**
 * Hover state for the single-client POI cluster bubbles. The hover machinery is shared with
 * the Network Explorer's bubbles (useClusterCardHover); this hook supplies the two cluster
 * layers, the per-source keys and the summary reader. The per-status breakdown is read from
 * the cluster's accumulated count_* properties (see entityClusterProperties), so no async
 * leaf query is needed.
 */
export function usePoiClusterInteractions(
    mapRef: RefObject<MapRef | null>,
    isMapLoaded: boolean,
    enabled: boolean = true,
): PoiClusterInteractionState {
    const { hoveredSummary, hoveredLngLat, onCardEnter, onCardLeave } = useClusterCardHover(
        mapRef,
        isMapLoaded,
        enabled,
        SINGLE_CLIENT_CLUSTER_OPTIONS,
    );
    return {
        hoveredEntitySummary: hoveredSummary,
        hoveredClusterLngLat: hoveredLngLat,
        onClusterCardEnter: onCardEnter,
        onClusterCardLeave: onCardLeave,
    };
}
